import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import throttle from 'lodash/throttle';
import { UploadedProviderDocument, getProviderDocumentDownloadLink, uploadedProviderDocumentTypeLabels } from '@uplift/api';
import { useUserQuery } from '@uplift/queries';
import { Loader, PDFPage, PDFPagination, ScrollableView, elementOnScreenPercentage, usePDFDocument, useBreakpoint } from '@uplift/ui-v2';
import { DocumentLayout } from './components';


export const UploadedProviderDocumentPreview: React.VFC<{
  document: UploadedProviderDocument;
}> = ({ document }) => {
  const breakpoint = useBreakpoint();
  const { data: downloadLink } = useSuspenseQuery({
    queryKey: [ 'providerDocumentDownloadLink', document.id ],
    queryFn: () => getProviderDocumentDownloadLink(document.id),
    select: (res) => res.data.downloadLink,
    staleTime: 5 * 60 * 1000,
  });

  const { data: provider } = useUserQuery(document.providerId, { suspense: true });
  const { data: pdf } = usePDFDocument(downloadLink);
  const [ page, setPage ] = React.useState(0);

  const pages = React.useMemo(() => Array.from({ length: pdf?.numPages ?? 0 }).map((_, index) => index), [ pdf ]);
  const pageElementRefs = React.useRef<HTMLDivElement[]>([]);

  React.useEffect(() => {
    const handleScroll = throttle(() => {
      const items = pageElementRefs.current.map((element, index) => ({
        key: index,
        value: elementOnScreenPercentage(element),
      })).sort((first, second) => second.value - first.value);

      if (items.length) {
        setPage(items[0].key);
      }
    }, 300);

    window.document.addEventListener('scroll', handleScroll, true);
    window.document.dispatchEvent(new Event('scroll'));

    return () => window.document.removeEventListener('scroll', handleScroll, true);
  }, []);

  const label = uploadedProviderDocumentTypeLabels[document.documentType] ?? document.documentType;

  return (
    <Box display="flex" height="100vh" flexDirection="row" flex={1}>
      <DocumentLayout
        title={label}
        subtitle={provider ? `uploaded by ${provider.firstName[0]}. ${provider.lastName}` : undefined}
        contentSpacing={false}
        controls={pdf && (
          <Box>
            <Divider />
            <Box p={2} textAlign="center">
              <Typography>Page {page + 1}/{pdf.numPages}</Typography>
            </Box>
          </Box>
        )}
      >
        {document.description && (
          <Box p={2} bgcolor="grey.50" display="flex" alignItems="center">
            <Typography>Description: {document.description}</Typography>
          </Box>
        )}
        <Divider />
        <Box width="100%" display="flex" flexDirection="column" gap="16px" pt={2}>
          {pdf && pages.map(pageNumber => (
            <React.Suspense key={pageNumber} fallback={<Loader color="secondary" height="500px" />}>
              <div
                ref={element => {
                  if (element) {
                    pageElementRefs.current[pageNumber] = element;
                  }
                }}
              >
                <PDFPage pdf={pdf} pageNumber={pageNumber} zoomAndDrag={true} />
              </div>
            </React.Suspense>
          ))}
        </Box>
      </DocumentLayout>
      {pdf && pdf.numPages > 1 && breakpoint.md && (
        <ScrollableView height="100%" maxWidth="150px" minWidth="112px">
          <Box display="flex" flex="1" pb={8} pt={3} justifyContent="center">
            <Box maxWidth="70%">
              <PDFPagination
                pdf={pdf}
                pageNumber={page}
                setPage={(pageNumber) => {
                  pageElementRefs.current[pageNumber]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
                }}
              />
            </Box>
          </Box>
        </ScrollableView>
      )}
    </Box>
  );
};
